import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { photoModerationService, REJECTION_REASONS } from '../services/photoModerationService'
import { PhotoReviewItem, ModerationStats } from '../lib/supabase'

const ProfilePhotoReview: React.FC = () => {
  const { user } = useAuth()
  const [pendingPhotos, setPendingPhotos] = useState<PhotoReviewItem[]>([])
  const [stats, setStats] = useState<ModerationStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [processing, setProcessing] = useState<string | null>(null)
  const [showRejectModal, setShowRejectModal] = useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [fullscreenImage, setFullscreenImage] = useState<{ url: string; name: string } | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError('') // Clear previous errors
      const [photosData, statsData] = await Promise.all([
        photoModerationService.getPendingPhotos(),
        photoModerationService.getModerationStats()
      ])
      setPendingPhotos(photosData)
      setStats(statsData)
    } catch (err) {
      console.error('Fetch data error:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data'
      setError(`Failed to fetch data: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (moderationId: string) => {
    if (!user?.id) return

    try {
      setProcessing(moderationId)
      const result = await photoModerationService.approvePhoto(moderationId, user.id)
      
      if (result.success) {
        // Delete old photo from storage if provided
        if (result.old_photo_url) {
          await photoModerationService.deleteImageFromStorage(result.old_photo_url)
        }
        
        // Refresh data
        await fetchData()
      } else {
        setError(result.message || 'Failed to approve photo')
      }
    } catch (err) {
      console.error('Approval error:', err)
      setError(err instanceof Error ? err.message : 'Failed to approve photo')
    } finally {
      setProcessing(null)
    }
  }

  const handleReject = async (moderationId: string) => {
    if (!user?.id) return

    const reason = rejectionReason === 'Other' ? customReason : rejectionReason
    if (!reason.trim()) {
      setError('Please provide a rejection reason')
      return
    }

    try {
      setProcessing(moderationId)
      const result = await photoModerationService.rejectPhoto(moderationId, user.id, reason)
      
      if (result.success) {
        // Delete rejected photo from storage if provided
        if (result.photo_url_to_delete) {
          await photoModerationService.deleteImageFromStorage(result.photo_url_to_delete)
        }
        
        // Close modal and refresh data
        setShowRejectModal(null)
        setRejectionReason('')
        setCustomReason('')
        await fetchData()
      } else {
        setError(result.message || 'Failed to reject photo')
      }
    } catch (err) {
      console.error('Rejection error:', err)
      setError(err instanceof Error ? err.message : 'Failed to reject photo')
    } finally {
      setProcessing(null)
    }
  }

  const openRejectModal = (moderationId: string) => {
    setShowRejectModal(moderationId)
    setRejectionReason('')
    setCustomReason('')
    setError('')
  }

  const closeRejectModal = () => {
    setShowRejectModal(null)
    setRejectionReason('')
    setCustomReason('')
    setError('')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTimeAgo = (dateString: string) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60)
      return `${hours} hour${hours > 1 ? 's' : ''} ago`
    } else {
      const days = Math.floor(diffInMinutes / 1440)
      return `${days} day${days > 1 ? 's' : ''} ago`
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Profile Picture Review</h1>
        <p className="text-sm sm:text-base text-gray-600">Review and moderate user profile pictures</p>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{stats.pending_count}</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending Review</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.pending_count}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{stats.approved_count}</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Approved</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.approved_count}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{stats.rejected_count}</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Rejected</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.rejected_count}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{stats.approved_today + stats.rejected_today}</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Reviewed Today</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.approved_today + stats.rejected_today}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Photos */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Pending Photos ({pendingPhotos.length})
            </h3>
            <button
              onClick={fetchData}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Refresh
            </button>
          </div>

          {pendingPhotos.length === 0 ? (
            <div className="text-center py-8">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No pending photos</h3>
              <p className="mt-1 text-sm text-gray-500">All profile pictures have been reviewed.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-4">
              {pendingPhotos.map((photo) => (
                <div key={photo.moderation_id} className="border border-gray-200 rounded-lg p-3 hover:shadow-lg transition-all hover:border-indigo-300 bg-white">
                  {/* Compact Photo Display */}
                  <div className="mb-3">
                    <div className="flex items-start space-x-3">
                      {/* New Photo - Clickable */}
                      <div className="relative flex-shrink-0">
                        <img
                          src={photo.photo_url}
                          alt={`${photo.user_name}'s profile picture`}
                          className="w-24 h-24 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity ring-2 ring-blue-400"
                          onClick={() => setFullscreenImage({ url: photo.photo_url, name: `${photo.user_name} - New Photo` })}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.src = 'https://via.placeholder.com/200x200?text=Image+Not+Found'
                          }}
                        />
                        <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full font-semibold">
                          New
                        </div>
                        <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-md">
                          <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </div>
                      </div>

                      {/* Current Photo - Clickable (if exists) */}
                      {photo.current_photo_url && (
                        <div className="relative flex-shrink-0">
                          <img
                            src={photo.current_photo_url}
                            alt="Current profile picture"
                            className="w-24 h-24 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity ring-2 ring-gray-300"
                            onClick={() => setFullscreenImage({ url: photo.current_photo_url!, name: `${photo.user_name} - Current Photo` })}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.src = 'https://via.placeholder.com/64x64?text=No+Photo'
                            }}
                          />
                          <div className="absolute -top-1 -left-1 bg-gray-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                            Old
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Compact User Information */}
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">{photo.user_name}</h4>
                    <p className="text-xs text-gray-600 truncate">{photo.user_email}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-gray-500">{photo.mobile}</p>
                      {photo.age && photo.gender && (
                        <p className="text-xs text-gray-500">{photo.age}y, {photo.gender}</p>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {getTimeAgo(photo.submitted_at)}
                    </p>
                  </div>

                  {/* Compact Action Buttons */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleApprove(photo.moderation_id)}
                      disabled={processing === photo.moderation_id}
                      className="flex-1 bg-green-600 text-white px-2 py-1.5 rounded-md text-xs font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {processing === photo.moderation_id ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Approve
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => openRejectModal(photo.moderation_id)}
                      disabled={processing === photo.moderation_id}
                      className="flex-1 bg-red-600 text-white px-2 py-1.5 rounded-md text-xs font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="p-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Profile Picture</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for rejection:
                </label>
                <select
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select a reason</option>
                  {REJECTION_REASONS.map((reason) => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              {rejectionReason === 'Other' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom reason:
                  </label>
                  <textarea
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Please specify the reason..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={3}
                  />
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => handleReject(showRejectModal)}
                  disabled={processing === showRejectModal || !rejectionReason || (rejectionReason === 'Other' && !customReason.trim())}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing === showRejectModal ? 'Rejecting...' : 'Reject Photo'}
                </button>
                <button
                  onClick={closeRejectModal}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Fullscreen Image Viewer */}
      {fullscreenImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setFullscreenImage(null)}
        >
          <div className="relative max-w-7xl max-h-full">
            {/* Close Button */}
            <button
              onClick={() => setFullscreenImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Image Title */}
            <div className="absolute -top-12 left-0 text-white text-lg font-medium">
              {fullscreenImage.name}
            </div>
            
            {/* Image */}
            <img
              src={fullscreenImage.url}
              alt={fullscreenImage.name}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Helper Text */}
            <div className="absolute -bottom-10 left-0 right-0 text-center text-gray-400 text-sm">
              Click anywhere outside to close
            </div>
          </div>
        </div>
      )}
      
    </div>
  )
}

export default ProfilePhotoReview
